/**
 * Admin System - Activity Logs
 * View admin activity history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, User, Calendar, Filter, RefreshCw,
  Loader2, ChevronDown, Shield, Trash2, Edit, Plus,
  Ban, CheckCircle, XCircle, Eye
} from 'lucide-react';
import { useAdmin } from './AdminContext';

const AdminLogs = () => {
  const { apiCall, formatDate } = useAdmin();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?action_type=${filter}` : '';
      const data = await apiCall('get', `/api/admin/logs${params}`);
      setLogs(data.logs || data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  }, [apiCall, filter]);
  
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  const getActionIcon = (action) => {
    const icons = {
      create: <Plus className="w-4 h-4 text-green-500" />,
      update: <Edit className="w-4 h-4 text-blue-500" />,
      delete: <Trash2 className="w-4 h-4 text-red-500" />,
      ban: <Ban className="w-4 h-4 text-red-500" />,
      unban: <CheckCircle className="w-4 h-4 text-green-500" />,
      verify: <Shield className="w-4 h-4 text-green-500" />,
      reject: <XCircle className="w-4 h-4 text-red-500" />,
      approve: <CheckCircle className="w-4 h-4 text-green-500" />,
      view: <Eye className="w-4 h-4 text-gray-500" />,
    };
    return icons[action] || <FileText className="w-4 h-4 text-gray-500" />;
  };
  
  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-500/10 border-green-500/30',
      update: 'bg-blue-500/10 border-blue-500/30',
      delete: 'bg-red-500/10 border-red-500/30',
      ban: 'bg-red-500/10 border-red-500/30',
      unban: 'bg-green-500/10 border-green-500/30',
      verify: 'bg-green-500/10 border-green-500/30',
      reject: 'bg-red-500/10 border-red-500/30',
      approve: 'bg-green-500/10 border-green-500/30',
    };
    return colors[action] || 'bg-gray-500/10 border-gray-500/30';
  };
  
  const actionTypes = [
    { id: 'all', label: 'All Actions' },
    { id: 'create', label: 'Create' },
    { id: 'update', label: 'Update' },
    { id: 'delete', label: 'Delete' },
    { id: 'ban', label: 'Ban' },
    { id: 'verify', label: 'Verify' },
    { id: 'approve', label: 'Approve' },
    { id: 'reject', label: 'Reject' },
  ];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Activity Logs
          </h2>
          <p className="text-sm text-muted-foreground">
            Track admin actions and system changes
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${
              showFilters ? 'bg-primary text-primary-foreground' : 'border-border hover:bg-secondary'
            }`}
          >
            <Filter className="w-4 h-4" />
          </button>
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-4 bg-secondary/30 rounded-lg">
          {actionTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setFilter(type.id)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filter === type.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border hover:bg-secondary'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      )}
      
      {/* Logs Timeline */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No activity logs found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {logs.map((log, index) => (
              <div 
                key={log.id || index}
                className="p-4 hover:bg-secondary/20 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Action Icon */}
                  <div className={`p-2 rounded-lg border ${getActionColor(log.action_type)}`}>
                    {getActionIcon(log.action_type)}
                  </div>
                  
                  {/* Log Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium">
                          {log.description || `${log.action_type} on ${log.target_type}`}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {log.admin_name || 'System'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(log.created_at)}
                          </span>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                        log.action_type === 'create' ? 'bg-green-500/20 text-green-600' :
                        log.action_type === 'delete' ? 'bg-red-500/20 text-red-600' :
                        log.action_type === 'update' ? 'bg-blue-500/20 text-blue-600' :
                        'bg-gray-500/20 text-gray-600'
                      }`}>
                        {log.action_type}
                      </span>
                    </div>
                    
                    {/* Additional Details */}
                    {log.details && (
                      <div className="mt-2 p-2 bg-secondary/30 rounded text-xs font-mono overflow-x-auto">
                        {typeof log.details === 'object' 
                          ? JSON.stringify(log.details, null, 2).slice(0, 200)
                          : log.details.slice(0, 200)
                        }
                        {(typeof log.details === 'string' ? log.details.length : JSON.stringify(log.details).length) > 200 && '...'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Load More */}
      {logs.length >= 100 && (
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Showing last 100 entries. Use filters to narrow results.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminLogs;
